import { useState } from 'react'
import { type LinearGradient } from '@backend/lib/db/typeRoots/layoutNode'
import { ColorPickerQuickmenu } from '@/components/ui/ColorPicker'
import { NumberInput } from './number-input'
import { GradientBar } from './gradient-bar'
import { getColorAtPosition } from './gradient-utils'
import { type Color } from '@backend/lib/db/typeRoots/color'

interface GradientPickerProps {
  gradient: LinearGradient
  onChange: (gradient: LinearGradient, isHoverSelection?: boolean) => void
  colorPalette: string[]
}

export function GradientPicker({ gradient, onChange, colorPalette }: GradientPickerProps) {
  const [selectedStopIndex, setSelectedStopIndex] = useState(0)

  const safeIndex = Math.min(selectedStopIndex, gradient.stops.length - 1)
  const selectedStop = gradient.stops[safeIndex]!

  const updateStops = (
    updater: (stops: LinearGradient['stops']) => LinearGradient['stops'],
    isHover?: boolean,
  ) => {
    onChange({ ...gradient, stops: updater([...gradient.stops]) }, isHover)
  }

  const handleMoveStop = (index: number, position: number, isHover: boolean) => {
    updateStops((stops) => {
      const newStops = [...stops]
      newStops[index] = { ...newStops[index]!, position }
      return newStops
    }, isHover)
  }

  const handleAddStop = (position: number) => {
    const color = getColorAtPosition(gradient.stops, position)
    const newStops = [...gradient.stops, { color, position }]
    setSelectedStopIndex(newStops.length - 1)
    onChange({ ...gradient, stops: newStops })
  }

  const handleRemoveStop = () => {
    if (gradient.stops.length <= 2) return
    const newStops = gradient.stops.filter((_, i) => i !== safeIndex)
    const newIndex = Math.min(safeIndex, newStops.length - 1)
    setSelectedStopIndex(newIndex)
    onChange({ ...gradient, stops: newStops })
  }

  const handleStopColorChange = (color: Color, isHover?: boolean) => {
    updateStops((stops) => {
      const newStops = [...stops]
      newStops[safeIndex] = { ...newStops[safeIndex]!, color }
      return newStops
    }, isHover)
  }

  return (
    <div className="flex flex-col gap-3">
      <GradientBar
        stops={gradient.stops}
        selectedStopIndex={safeIndex}
        onSelectStop={setSelectedStopIndex}
        onMoveStop={handleMoveStop}
        onAddStop={handleAddStop}
      />

      <div className="flex items-center gap-2">
        <span className="text-foreground text-xs">Angle</span>
        <NumberInput
          value={gradient.angle}
          onChange={(value, isHover) => onChange({ ...gradient, angle: value }, isHover)}
          onBlur={(value) => onChange({ ...gradient, angle: value })}
          min={0}
          max={360}
          step={1}
        />
      </div>

      <div className="flex items-center gap-2">
        <span className="text-foreground text-xs">Hold</span>
        <NumberInput
          key={safeIndex}
          value={selectedStop.smoothness ?? 0}
          onChange={(value, isHover) =>
            updateStops((stops) => {
              const newStops = [...stops]
              newStops[safeIndex] = { ...newStops[safeIndex]!, smoothness: value }
              return newStops
            }, isHover)
          }
          onBlur={(value) =>
            updateStops((stops) => {
              const newStops = [...stops]
              newStops[safeIndex] = { ...newStops[safeIndex]!, smoothness: value }
              return newStops
            })
          }
          min={0}
          max={100}
          step={1}
        />
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <span className="text-foreground text-xs">Stop color</span>
          {gradient.stops.length > 2 && (
            <button
              onClick={handleRemoveStop}
              className="text-foreground hover:bg-hover rounded-md px-1.5 py-0.5 text-xs">
              Remove
            </button>
          )}
        </div>
        <ColorPickerQuickmenu
          value={selectedStop.color}
          onChange={handleStopColorChange}
          colorPalette={colorPalette}
        />
      </div>
    </div>
  )
}
